import {useUpdatedRef} from '@sail/react';
import {noop, once} from '@sail/utils';
import {useMemo} from 'react';
import {UninitializedObservabilityProviderError} from 'src/internal/common/errors';
import logErrorInDev from 'src/internal/common/logErrorInDev';
import {useObservabilityApi} from 'src/internal/provider/ObservabilityApiProvider';

import type {IReports} from 'src/internal/errors/types';
import type {IObservabilityApi} from 'src/internal/types';

const logUninitializedErrorOnce = once(() => {
  logErrorInDev(new UninitializedObservabilityProviderError('useReports'));
});

const defaultReports: IReports = {
  error: noop,
  warning: noop,
};

// WeakMap to store reports objects by provider instance
const providerReportsMap = new WeakMap<IObservabilityApi, IReports>();

/**
 * @see https://trailhead.corp.stripe.com/docs/sail-observability/api/useReports
 */
export default function useReports(): IReports {
  const api = useObservabilityApi();
  const apiRef = useUpdatedRef(api);

  return useMemo((): IReports => {
    // eslint-disable-next-line react-hooks/refs
    const currentApiValue = apiRef.current;

    // eslint-disable-next-line react-hooks/refs
    if (!currentApiValue) {
      logUninitializedErrorOnce();

      return defaultReports;
    }

    // Check if we already have reports for this provider
    let reports = providerReportsMap.get(currentApiValue);

    if (!reports) {
      reports = {
        error(...args: Parameters<IReports['error']>): void {
          return apiRef.current?.reports?.error(...args);
        },

        warning(...args: Parameters<IReports['warning']>): void {
          return apiRef.current?.reports?.warning(...args);
        },
      };

      // eslint-disable-next-line react-hooks/refs
      providerReportsMap.set(currentApiValue, reports);
    }

    return reports;
  }, [
    apiRef, // This is a ref object, so it never changes and thus the returned object is stable.
  ]);
}
